import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ASSIGNED_SALESPERSON, getFormattedDisplayName, UsersService } from '../../../../data-services/users.service';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';

@Injectable({ providedIn: 'root' })
export class CreateOpportunityService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    private readonly usersService: UsersService,
  ) {
    super();
  }

  getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): ActivityTableEntry {
    return this.tableEntry(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.ACTIVITY.SUBTITLE'),
      this.getDefaultStepColumnBody(namespace, activity.workflowStep, automation),
    );
  }

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};
    // if any required data is missing (eg. automation made from a template, set validation errors)
    if (
      !data.assigned_salesperson_id ||
      !data.opportunity_name ||
      !data.line_items ||
      !(data.expected_close_date_num_days >= 0) ||
      !data.pipeline_id ||
      !(data.probability >= 0)
    ) {
      const message = this.buildMessageFromTemplateData(data);
      return this.nodeDisplayData(message, this.validationErrorsDetected(step, '', true));
    }
    let name$ = this.usersService.getUsersById([data.assigned_salesperson_id]).pipe(
      take(1),
      map((users) => getFormattedDisplayName(users[0])),
      catchError(() => of('')),
    );
    if (data.assigned_salesperson_id === ASSIGNED_SALESPERSON) {
      name$ = this.translateService.stream('AUTOMATIONS.COMMON.ASSIGNED_SALESPERSON');
    }

    return name$.pipe(
      switchMap((name) => {
        if (name) {
          const assignedSalesperson = this.translateService.instant('AUTOMATIONS.COMMON.ASSIGNED_SALESPERSON');
          if (name.trim() === assignedSalesperson) {
            name = name.toLowerCase();
          }
          return this.nodeDisplayData(
            this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.ACTION.NODE_TITLE', {
              opportunity_name: data.opportunity_name,
              assignee_name: name,
            }),
            this.noValidationErrorsDetected(),
          );
        }
        // if there's no assignee set (required), then something is wrong
        return this.nodeDisplayData(
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.ACTION.NODE_TITLE_FOR_TEMPLATE'),
          this.validationErrorsDetected(step, '', true),
        );
      }),
    );
  }

  getTemplateNodeData(namespace: string, step: WorkflowStepInterface, _isWaitUntilNode: boolean): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};
    const message = this.buildMessageFromTemplateData(data);
    return this.nodeDisplayData(message, this.noValidationErrorsDetected());
  }

  private buildMessageFromTemplateData(data): Observable<string> {
    let message = this.translateService.stream(
      'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.ACTION.NODE_TITLE_FOR_TEMPLATE',
    );

    if (!!data.opportunity_name && !!data.assigned_salesperson_id) {
      message = this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.ACTION.NODE_TITLE', {
        opportunity_name: data.opportunity_name,
        assignee_name: this.translateService.instant('AUTOMATIONS.COMMON.ASSIGNED_SALESPERSON').toLowerCase(),
      });
    } else if (data.opportunity_name) {
      message = this.translateService.stream(
        'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.ACTION.NODE_TITLE_NAME_ONLY',
        {
          opportunity_name: data.opportunity_name,
        },
      );
    } else if (data.assigned_salesperson_id) {
      message = this.translateService.stream(
        'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.ACTION.NODE_TITLE_ASSIGNEE_ONLY',
        {
          assignee_name: this.translateService.instant('AUTOMATIONS.COMMON.ASSIGNED_SALESPERSON').toLowerCase(),
        },
      );
    }
    return message;
  }
}
